import {
  playbackContextFromUrl,
  Renderer,
  RendererHost,
  RendererHostClientStub,
  RendererMessageProxyKey,
} from '@fullstory/renderer-shared';
import { MobilePageRenderer } from './mobile/renderer';
import { WebPageRenderer } from './web/renderer';
import { Inner } from '@fullstory/channel/fireandforget';
import { CreateClient, Dispatcher, ProxiedFunctionCall } from '@fullstory/channel/proxy';
import { addPlaybackServiceWorkerListener } from '@fullstory/controlled-iframe';
import { isCypressInTop } from '@fullstory/util/domains';
import { MessageProxyServer } from '@fullstory/channel';
import { ServerPageRenderer } from './server/renderer';

export async function Start(): Promise<void> {
  let inTest: boolean = false;

  try {
    if (window.top.document) {
      // We didn't throw. This is bad because we are not protected by cross domain policy.
      // Our cypress tests relax same origin protection, so only allow this to proceed if we know we are in that env.
      inTest = isTestEnvironment();
      if (inTest) {
        console.log('Test environment.');
      } else {
        console.log(
          `Renderer IFrame is same origin. (${window.top.location.host}) Aborting playback.`,
        );
        return;
      }
    }
  } catch (e) {
    // Good! This means this is off domain and cross origin protections are working. Proceed.
  }

  const proxyServer = new MessageProxyServer<Renderer>(window, RendererMessageProxyKey);
  const ch = Inner<ProxiedFunctionCall>();
  const dispatcher = new Dispatcher(ch);
  const host = CreateClient<RendererHost>(ch, new RendererHostClientStub(), 'RendererHost');

  setUpLoggingHooks(host);

  const renderer = await createRenderer(host, dispatcher);
  return proxyServer.registerHandler(renderer);
}

async function createRenderer(host: RendererHost, dispatcher: Dispatcher): Promise<Renderer> {
  const url = new URL(window.location.href);
  const ctx = await playbackContextFromUrl(url);
  if (!ctx) {
    throw new Error(`Invalid playback context`);
  }

  // Listen for service worker messages on the top-level playback iframe. This
  // gives us an opportunity to rewrite resources even if they get loaded
  // outside of the web renderer.
  addPlaybackServiceWorkerListener(ctx, window, {
    log(level: keyof Console, message: string): void {
      // Bounce log messages to the host so they show up in e.g. reftest logs.
      host.log(level, `[SERVICE WORKER]: ${message}`);
    },
    recordCustomEvent(name: string, properties: { [name: string]: any }) {
      host.recordCustomEvent(name, properties);
    },
  });

  switch (ctx.page.pageType) {
    case 'android':
    case 'ios':
    case 'mobilemock':
      return new MobilePageRenderer(host, dispatcher, ctx);
    case 'web':
    case 'unknown': // From very old sessions.
    case null:
    case undefined:
      return new WebPageRenderer(host, dispatcher, ctx);
    case 'server':
      return new ServerPageRenderer(host, dispatcher, ctx);
    default:
      throw new Error(`Unexpected renderer type '${ctx.page.pageType}'`);
  }
}

function isTestEnvironment(): boolean {
  if (isCypressInTop(window)) {
    return true;
  }

  const testHosts = [
    'www.fullstory.test:8043',
    'app.fullstory.test:8043',
    'play.fullstory.test:8043',
    'localhost:8043',
    'test.fullstory.test:8888',
    'test-the-things.com:8888',
  ];

  const actualHost = window.top.location.host;
  for (const testHost of testHosts) {
    if (actualHost == testHost) {
      return true;
    }
  }

  return false;
}

type consoleKey = 'debug' | 'log' | 'info' | 'warn' | 'error';

const hookConsoleMethod = (level: consoleKey, host: RendererHost) => {
  const native = console[level];

  // Bounce log messages to the host.
  console[level] = (...args: any[]) => {
    const message = args.join(' ');
    host.log(level, message);
    native.apply(console, args);
  };
};
/**
 * Hook the console logging functions and bounce them to the host. We do this in
 * test environments to give test frameworks an opportunity to see these logging
 * messages, and in production to make these messages available via the
 * FSPlayback console API.
 */
function setUpLoggingHooks(host: RendererHost): void {
  // Hook console.log() and friends.
  for (const level of ['debug', 'log', 'info', 'warn', 'error'] as consoleKey[]) {
    hookConsoleMethod(level, host);
  }
}
